'use client';

import { useMemo } from 'react';
import { Placement } from '@floating-ui/react';
import { cn } from '@lib/utils';
import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTooltip } from './useTooltip';

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  position?: Placement;
  className?: string;
  contentClassName?: string;
}

/**
 * 툴팁 UI 컴포넌트 (Floating UI 사용) https://floating-ui.com/docs/react
 * 부모 요소의 overflow-hidden 속성에 영향받지 않고 정확한 위치에 표시됩니다.
 *
 * @param {TooltipProps} props - 툴팁 컴포넌트 프롭스
 * @param {ReactNode} props.children - 툴팁을 표시할 기준 요소(트리거)
 * @param {ReactNode} props.content - 툴팁에 표시할 내용
 * @param {Placement} props.position - 툴팁 표시 위치 (기본값: bottom)
 * @param {string} props.className - 기준 요소에 적용할 추가 클래스명
 * @param {string} props.contentClassName - 툴팁 컨텐츠에 적용할 추가 클래스명
 * @returns {JSX.Element} 툴팁 컴포넌트
 */
const Tooltip = ({
  children,
  content,
  position = 'bottom',
  className = '',
  contentClassName = '',
}: TooltipProps) => {
  const { isOpen, x, y, refs, getReferenceProps, getFloatingProps } =
    useTooltip({
      position,
    });

  const floatingStyle = useMemo(
    () => ({ position: 'fixed' as const, top: y ?? 0, left: x ?? 0 }),
    [x, y],
  );

  return (
    <>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={cn('inline-block', className)}
      >
        {children}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            className={cn(
              'z-[9999] max-w-[300px] rounded-md bg-black px-3 py-2 text-xs font-medium leading-[18px] tracking-[0.016px] text-white shadow-md',
              contentClassName,
            )}
            style={floatingStyle}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
